import { useState } from 'react'
import type { ArcanaReadingQueueItemDto } from '@lunary/api-client'
import { useVoiceRecorder } from '@lunary/ui-web/voice-recorder'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { CardPicker } from './card-picker'
import { CloseOfferButton } from './close-offer-button'
import { errorText } from './labels'
import { useReadingActions } from './queries'
import { VoiceRecorderBox } from './voice-recorder-box'
import styles from './readings.module.css'

const VOICE_MAX_MS = 10 * 60_000
const TEXT_MAX = 8000
const CAPTION_MAX = 1000

type Props = { reading: ArcanaReadingQueueItemDto; voiceEnabled: boolean }

function closedNote(r: ArcanaReadingQueueItemDto): string | null {
  if (r.status === 'closed') return 'Разбор завершён.'
  if (r.status === 'rejected') return 'Вопрос отклонён, оплата вернулась человеку.'
  return null
}

export function ExpertComposer({ reading, voiceEnabled }: Props) {
  const actions = useReadingActions(reading.id)
  const rec = useVoiceRecorder(VOICE_MAX_MS)
  const [mode, setMode] = useState<'voice' | 'text'>(voiceEnabled ? 'voice' : 'text')
  const [text, setText] = useState('')
  const [caption, setCaption] = useState('')
  const [cards, setCards] = useState<number[]>([])
  const [confirm, setConfirm] = useState(false)

  const note = closedNote(reading)
  if (note) {
    return (
      <div className={styles.closedNote}>
        <span>{note}</span>
        <div className={styles.offerRow}>
          <CloseOfferButton reading={reading} />
        </div>
      </div>
    )
  }

  const clarifying = reading.status === 'clarifying'
  const answered = clarifying || reading.status === 'ready'
  const busy = actions.voice.isPending || actions.text.isPending || actions.start.isPending
  const error = actions.voice.error ?? actions.text.error ?? actions.start.error
  const reset = () => {
    setText('')
    setCaption('')
    setCards([])
  }
  const sendVoice = () => {
    if (!rec.recording) return
    actions.voice.mutate({ blob: rec.recording.blob, caption: caption.trim(), cards }, { onSuccess: () => { rec.discard(); reset() } })
  }
  const sendText = () => actions.text.mutate({ body: text.trim(), cards }, { onSuccess: reset })

  return (
    <div className={styles.composer}>
      <div className={styles.composerHead}>
        <span className={styles.composerTitle}>{answered ? (reading.format === 'consultation' ? 'Консультация · переписка' : 'Переписка') : reading.format === 'consultation' ? 'Консультация' : 'Разбор'}</span>
        {reading.status === 'pending' && (
          <button type="button" className="secondary" disabled={busy} onClick={() => actions.start.mutate()}>
            {actions.start.isPending ? 'Беру…' : 'Взять в работу'}
          </button>
        )}
        {answered && <CloseOfferButton reading={reading} />}
        {!answered && (
          <button type="button" className="danger" disabled={busy} onClick={() => setConfirm(true)}>
            Отклонить
          </button>
        )}
        {voiceEnabled && (
          <button type="button" className={styles.linkBtn} onClick={() => setMode(mode === 'voice' ? 'text' : 'voice')} disabled={rec.status === 'recording' || rec.status === 'paused'}>
            {mode === 'voice' ? 'Ответить текстом' : 'Записать голосом'}
          </button>
        )}
      </div>

      {mode === 'voice' ? (
        <VoiceRecorderBox rec={rec} busy={busy} />
      ) : (
        <textarea rows={6} maxLength={TEXT_MAX} value={text} placeholder="Текст ответа" onChange={(e) => setText(e.target.value)} />
      )}

      {mode === 'voice' && rec.status === 'preview' && (
        <input type="text" maxLength={CAPTION_MAX} value={caption} placeholder="Подпись к голосовому (необязательно)" onChange={(e) => setCaption(e.target.value)} />
      )}
      <CardPicker value={cards} onChange={setCards} />
      {(mode === 'text' || rec.status === 'preview') && (
        <>
          <div className={styles.sendRow}>
            {mode === 'voice' ? (
              <button type="button" disabled={busy || !rec.recording} onClick={sendVoice}>
                {actions.voice.isPending ? 'Отправляю…' : clarifying ? 'Отправить ответ' : 'Отправить разбор'}
              </button>
            ) : (
              <button type="button" disabled={busy || text.trim().length === 0} onClick={sendText}>
                {actions.text.isPending ? 'Отправляю…' : 'Отправить текстом'}
              </button>
            )}
            {!voiceEnabled && <span className="muted">Голосовые выключены: хранилище не настроено.</span>}
          </div>
        </>
      )}
      {error && <p className="error-box">{errorText(error)}</p>}

      <ConfirmDialog
        open={confirm}
        title="Отклонить вопрос?"
        text="Человек увидит, что разбор не состоится, и оплата вернётся ему."
        confirmLabel="Отклонить"
        danger
        pending={actions.reject.isPending}
        error={actions.reject.error ? errorText(actions.reject.error) : null}
        onConfirm={() => actions.reject.mutate(undefined, { onSuccess: () => setConfirm(false) })}
        onClose={() => setConfirm(false)}
      />
    </div>
  )
}
